// R72 — HER OWN FACE, AND THE THREE WAYS THIS CONTROL LIED ABOUT IT.
//
// ITEM 4(c) put the photo in My Room and `ProfilePhoto` has always READ ITS PAYLOAD on the two
// writes (R39's law, and it is followed). **The defects are one layer under that**, and the sweep
// R70 opened found all three by asking the question that census could not: *not what the client does
// with `ok: false`, but what the whole exchange looks like when it goes wrong.*
//
// **1. THE READ DESTROYED W5-2/R39's THIRD STATE, WHICH IS R59's SAVED SHELF ONE CONTROL OVER.**
// The mount was `.then((d) => { if (d?.ok) { setPhoto(d.photo || null); setLoaded(true); } })` with a
// `.catch` that only set `loaded`. On any refusal the photo stayed `null` — and `null` is what this
// control draws the HOUSE GLYPH for. So a member who HAS a photo was shown the glyph, the **Use the
// house glyph** button vanished (it renders only when `photo` is truthy), and **Change photo** read
// **Add a photo**, offering to add the thing she already added. Nothing was red anywhere.
//
// **2. THE REMOVE THREW AWAY A REAL BOOLEAN THE SERVER HAD ALREADY COMPUTED** (R35/R54/R61, the
// fourth time this house has found that exact shape). `removeAvatarPhoto` answers off `rowCount` and
// the route sent `{ ok: true, photo: null }` regardless — so on a database that refused the delete,
// her screen said the picture was gone **while her face was still riding beside her name in the
// Living Room for everybody else.** That is the worst direction this particular lie can point.
//
// **3. AND THE ROUTE COULD PRINT A RAW POSTGRES ERROR INTO HER ROOM.** `dispatchLivingRoom` had no
// `try`, so a thrown handler fell to index.js's last-resort, which answers `500 { message:
// error.message }` with **no `ok`** — and `choose()` renders `d?.message` directly. R65 found the
// same handler doing the same thing to the owner's dashboard. Closed above all 29 routes, and closed
// AGAIN here: **a message is the server's own word only when it arrives beside `ok: false`**, which
// is R53's presence guard pointed at the sentence rather than at the data.
//
// **THREE ANSWERS, NOT FOUR, AND IT IS ARGUED (R58/R70).** `enter()` composes a real sentence for
// every refusal this route can make, *including* the signed-out one ("Sign in first."). So the
// client's job is to CARRY the server's word, not to classify it a second time — a `sign-in` arm
// here would be the client re-deciding something the server has already worded, which is the second
// answer key R58 refuses. The one thing the server cannot word is a request that never left the
// phone, and that is what `offline` is for (R18).
//
// `doFetch` is a PARAMETER (R46), so every answer all three acts can give is reachable with no
// browser, no session and no database.
import { AVATAR_LOAD_FAILED, SAVE_FAILED } from "../livingRoom.js";

export type AvatarAnswer = {
  ok: boolean;
  kind: "ok" | "refused" | "offline";
  // Meaningful ONLY when `ok`. On a refusal it is null because nothing was learned — never because
  // she has no photo. The caller must not paint from it unless the answer landed.
  photo: string | null;
  // The SERVER's own sentence where it composed one, "" where it did not. Never a raw error.
  message: string;
};

export const avatarPath = "/api/living-room/avatar";

export type AvatarShot = { base64: string; contentType: string; width: number; height: number };

const refusal = (kind: "refused" | "offline", message = ""): AvatarAnswer => ({
  ok: false, kind, photo: null, message,
});

async function ask(doFetch: typeof fetch, init?: RequestInit): Promise<AvatarAnswer> {
  let response: Response;
  try {
    response = await doFetch(avatarPath, init);
  } catch {
    // R18/R63: a request that never landed is TRANSPORT NOISE, not the server's word about her face.
    return refusal("offline");
  }
  const data = await response.json().catch(() => null) as
    | { ok?: boolean; photo?: unknown; message?: string }
    | null;
  if (!response.ok || !data || data.ok !== true) {
    // **A MESSAGE IS THE SERVER'S OWN WORD ONLY WHEN IT ARRIVES BESIDE `ok: false`.** index.js's
    // last-resort handler sends `{ message: <raw error> }` with no `ok` at all, and this control
    // prints what it is handed — so an unworded body is treated as having said nothing.
    return refusal("refused", data?.ok === false && typeof data.message === "string" ? data.message : "");
  }
  return { ok: true, kind: "ok", photo: typeof data.photo === "string" ? data.photo : null, message: "" };
}

// WHAT HER FACE IS. `cache: "no-store"` because the URL carries a version and a stale one is a photo
// she has already replaced.
export function readAvatar(doFetch: typeof fetch = typeof fetch === "function" ? fetch : (undefined as never)) {
  return ask(doFetch, { cache: "no-store" });
}

export function saveAvatar(shot: AvatarShot, doFetch: typeof fetch = typeof fetch === "function" ? fetch : (undefined as never)) {
  return ask(doFetch, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ data: shot.base64, contentType: shot.contentType, width: shot.width, height: shot.height }),
  });
}

export function removeAvatar(doFetch: typeof fetch = typeof fetch === "function" ? fetch : (undefined as never)) {
  return ask(doFetch, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ remove: true }),
  });
}

// **ONE SENTENCE LAW FOR BOTH WRITES, WHICH IS WHAT KEEPS 14-B4's DECLARATION TRUE.** The `photo.`
// prefix is ENGLISH_BY_DESIGN because *every word this control says comes from `src/livingRoom.js`* —
// and the save's refusal was hand-typed in the component twice and in the route once. The server's
// words win where it sent any (R51/R60/R66: those sentences exist and had never reached her); the
// house's own line stands in where it did not.
export function avatarNote(answer: AvatarAnswer, fallback: string = SAVE_FAILED) {
  if (answer.ok) return "";
  return answer.message || fallback;
}

// A read that did not land says what it did NOT do, because the alternative — silence — is the glyph
// standing in for her face with no account of why (R66: an act that landed is not silent, and a read
// that failed under a picture of somebody is worse than one that failed under a list).
export function avatarLoadNote(answer: AvatarAnswer) {
  return avatarNote(answer, AVATAR_LOAD_FAILED);
}
